import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormName, formValueSelector } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { Utils } from '../../services';
import SelectField from '../SelectField/SelectField';
import MultiSelectField from '../MultiSelectField/MultiSelectField';
import i18n from '../../locales/i18n';

import './SalepointsField.css';

function selected_id_list(raw) {
    if (Array.isArray(raw))
        return raw.map(String).filter(Boolean);
    if (raw !== '' && raw != null)
        return [String(raw)];
    return [];
}

function salepoint_option_label(salepoint, t, { withEditionBadge = false } = {}) {
    const name = Utils.localized_or_fallback(salepoint.name, i18n.language)
        || String(salepoint._id || '');
    const edition = String(salepoint.edition || '').trim();
    if (withEditionBadge && edition) {
        return t('salepoints.other_edition_option', { name, edition });
    }
    return name;
}

function SalepointsFieldBody({ form, name, salepoints, onChange, _fixedWidth, single }) {
    const { t } = useTranslation();
    const selector = useMemo(
        () => (form ? formValueSelector(form) : null),
        [form]
    );
    const rawSelected = useSelector(state => (
        selector ? selector(state, name) : undefined
    ));
    const selectedIds = useMemo(() => selected_id_list(rawSelected), [rawSelected]);

    const editionIds = useMemo(
        () => new Set((salepoints || []).map(sp => String(sp._id))),
        [salepoints]
    );

    const [resolvedById, setResolvedById] = useState({});

    // Do NOT filter on an in-flight ref inside this memo: mutating a ref does not
    // recompute the memo, but a later recompute (e.g. salepoints list arriving)
    // would drop ids still fetching, cancel the effect, discard the response, and
    // never re-fetch — stuck on "Chargement du point de vente…".
    const missingIds = useMemo(
        () => selectedIds.filter(id =>
            !editionIds.has(id)
            && !(id in resolvedById)
        ),
        [selectedIds, editionIds, resolvedById]
    );
    const missing_ids_key = missingIds.join('|');

    useEffect(() => {
        if (!missingIds.length)
            return undefined;

        let cancelled = false;
        const to_fetch = missingIds;

        // Lazy import avoids a models ↔ components cycle at module load
        // (FormLayout → FileField → components → SalepointsField → Salepoints).
        Promise.all(to_fetch.map(id =>
            import('../../services/rest/Salepoints')
                .then(({ default: SalepointsApi }) => SalepointsApi.get(id))
                .then(json => ({ id, salepoint: json?.salepoint || null }))
                .catch(() => ({ id, salepoint: null }))
        )).then(results => {
            if (cancelled)
                return;
            setResolvedById(prev => {
                const next = { ...prev };
                results.forEach(({ id, salepoint }) => {
                    next[id] = salepoint
                        ? salepoint
                        : { _id: id, _unresolved: true };
                });
                return next;
            });
        });

        return () => {
            cancelled = true;
        };
    }, [missing_ids_key]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!salepoints)
        return null;

    const outOfEditionOptions = selectedIds
        .filter(id => !editionIds.has(id))
        .map(id => {
            const resolved = resolvedById[id];
            if (!resolved) {
                return {
                    key: id,
                    text: t('salepoints.other_edition_loading', { id }),
                    value: id,
                    disabled: true,
                };
            }
            if (resolved._unresolved) {
                return {
                    key: id,
                    text: `[${t('common.unknown')} - ${id}]`,
                    className: 'line-through',
                    value: id,
                };
            }
            return {
                key: resolved._id || id,
                text: salepoint_option_label(resolved, t, { withEditionBadge: true }),
                value: resolved._id || id,
                className: 'salepoint-other-edition',
            };
        });

    const options = [
        ...(salepoints || []).map(salepoint => ({
            key: salepoint._id,
            text: salepoint_option_label(salepoint, t),
            value: salepoint._id,
        })),
        ...outOfEditionOptions,
    ];

    const onFieldChange = (value) => {
        return onChange ? onChange(value) : true;
    };

    return (
        <div className="SalepointsField">
            {single ? (
                <SelectField
                    name={name}
                    options={options}
                    placeholder={t('salepoints.choose_a_salepoint')}
                    onSelectChange={onFieldChange}
                    clearable
                />
            ) : (
                <MultiSelectField
                    name={name}
                    options={options}
                    placeholder={t('salepoints.choose_a_salepoint')}
                    onSelectChange={onFieldChange}
                />
            )}
        </div>
    );
}

const SalepointsField = (props) => (
    <FormName>
        {({ form }) => <SalepointsFieldBody form={form} {...props} />}
    </FormName>
);

export default SalepointsField;
